import { Modal } from "@dropbox/dig-components/modal";
import { Text } from "@dropbox/dig-components/typography";
import type { PAPEvent } from "@mirage/analytics/events/base/event";
import type { ActionSurfaceComponent } from "@mirage/analytics/events/enums/action_surface_component";
import { DashThemeProvider } from "@mirage/service-settings/theming/DashThemeProvider";
import type { AssistCustomTemplate } from "@mirage/shared/compose/assist-template";
import type { ComposeVoice } from "@mirage/shared/compose/compose-voice";
import { useIsMobileSize } from "@mirage/shared/responsive/mobile";
import i18n from "@mirage/translations";
import type { ReactNode } from "react";
import { memo, useState } from "react";
import { AIToolLibraryMobileTabs } from "./AIToolLibraryMobileTabs";
import styles from "./AIToolLibraryModal.module.css";
import { AIToolLibrarySidebar } from "./AIToolLibrarySidebar";
import { PromptsTab } from "./PromptsTab";
import { TemplatesTab } from "./TemplatesTab";
import { TonesTab } from "./TonesTab";
import { UseTemplateConfirmationModal } from "./UseTemplateConfirmationModal";

export type AIToolLibraryTab = "templates" | "tones" | "prompts";

export interface AIToolLibraryModalProps {
  isOpen: boolean;
  activeTab: AIToolLibraryTab;
  onRequestClose: () => void;
  onSwitchTab: (tab: AIToolLibraryTab) => void;
  logComposeEvent: (
    event: PAPEvent,
    overrides?: {
      actionSurfaceComponent?: ActionSurfaceComponent;
    },
  ) => void;

  // Templates props
  templates: AssistCustomTemplate[];
  onUseTemplate: (templateID: string, templateName: string) => void;
  currentTemplateID?: string | null;
  saveTemplate: (
    template: AssistCustomTemplate,
    onSaved?: (savedTemplate: AssistCustomTemplate) => void,
  ) => Promise<void>;
  onDeleteTemplate: (templateId: string) => void;
  setCreatedTemplateSnackbarMsg: (msg: string | undefined) => void;
  templateSettingsSnackbar: ReactNode;

  // Tones props
  voices: ComposeVoice[];
  onUseVoice: (voiceID: string, voiceName: string) => void;
  currentVoiceID?: string | null;
  saveVoice: (voice: ComposeVoice) => Promise<void | string>;
  onDeleteVoice: (voiceId: string) => void;
  setCreatedVoiceSnackbarMsg: (msg: string | undefined) => void;
  voiceSettingsSnackbar: ReactNode;
}

export const AIToolLibraryModal = memo(
  ({
    isOpen,
    activeTab,
    onRequestClose,
    onSwitchTab,
    templates,
    onUseTemplate,
    currentTemplateID,
    saveTemplate,
    onDeleteTemplate,
    setCreatedTemplateSnackbarMsg,
    templateSettingsSnackbar,
    voices,
    onUseVoice,
    currentVoiceID,
    saveVoice,
    onDeleteVoice,
    setCreatedVoiceSnackbarMsg,
    voiceSettingsSnackbar,
    logComposeEvent,
  }: AIToolLibraryModalProps) => {
    const isMobileSize = useIsMobileSize();
    const [confirmationModalState, setConfirmationModalState] = useState<{
      isOpen: boolean;
      templateId: string;
      templateName: string;
    }>({
      isOpen: false,
      templateId: "",
      templateName: "",
    });

    const handleSaveTemplate = async (
      template: AssistCustomTemplate,
      onSaved?: (savedTemplate: AssistCustomTemplate) => void,
    ) => {
      await saveTemplate(template, (savedTemplate) => {
        // Show confirmation modal after successful save
        setConfirmationModalState({
          isOpen: true,
          templateId: savedTemplate.id,
          templateName: savedTemplate.name,
        });
        if (onSaved) {
          onSaved(savedTemplate);
        }
      });
    };

    const handleConfirmationYes = () => {
      // Use the template and close the main modal
      onUseTemplate(
        confirmationModalState.templateId,
        confirmationModalState.templateName,
      );
      onRequestClose();
      setConfirmationModalState({
        isOpen: false,
        templateId: "",
        templateName: "",
      });
    };

    const handleConfirmationNo = () => {
      // Just close the confirmation modal, keep main modal open
      setConfirmationModalState({
        isOpen: false,
        templateId: "",
        templateName: "",
      });
    };

    return (
      <Modal
        open={isOpen}
        onRequestClose={onRequestClose}
        className={styles.AIToolLibraryModal}
        width={1083}
        isCentered
        withCloseButton={"close"}
      >
        <DashThemeProvider>
          <Modal.Body className={styles.AIToolLibraryModalContent}>
            <div className={styles.AIToolLibraryHeader}>
              <div className={styles.AIToolLibraryHeaderTitle}>
                <Text
                  size="xlarge"
                  isBold
                  className={styles.AIToolLibraryHeaderTitleText}
                >
                  {i18n.t("ai_tool_library_title")}
                </Text>
              </div>
            </div>
            <div
              className={
                isMobileSize
                  ? styles.AIToolLibraryModalBodyMobile
                  : styles.AIToolLibraryModalBody
              }
            >
              {isMobileSize ? (
                <>
                  <AIToolLibraryMobileTabs
                    activeTab={activeTab}
                    onSwitchTab={onSwitchTab}
                  />
                  <div className={styles.AIToolLibraryContentMobile}>
                    {activeTab === "templates" && (
                      <TemplatesTab
                        templates={templates}
                        onUseTemplate={onUseTemplate}
                        currentTemplateID={currentTemplateID}
                        saveTemplate={handleSaveTemplate}
                        onDeleteTemplate={onDeleteTemplate}
                        setCreatedTemplateSnackbarMsg={
                          setCreatedTemplateSnackbarMsg
                        }
                        logComposeEvent={logComposeEvent}
                      />
                    )}
                    {activeTab === "tones" && (
                      <TonesTab
                        voices={voices}
                        onUseVoice={onUseVoice}
                        currentVoiceID={currentVoiceID}
                        saveVoice={saveVoice}
                        onDeleteVoice={onDeleteVoice}
                        setCreatedVoiceSnackbarMsg={setCreatedVoiceSnackbarMsg}
                        voiceSettingsSnackbar={voiceSettingsSnackbar}
                        logComposeEvent={logComposeEvent}
                      />
                    )}
                    {activeTab === "prompts" && <PromptsTab />}
                  </div>
                </>
              ) : (
                <>
                  <AIToolLibrarySidebar
                    activeTab={activeTab}
                    onSwitchTab={onSwitchTab}
                  />
                  <div className={styles.AIToolLibraryContent}>
                    {activeTab === "templates" && (
                      <TemplatesTab
                        templates={templates}
                        onUseTemplate={onUseTemplate}
                        currentTemplateID={currentTemplateID}
                        saveTemplate={handleSaveTemplate}
                        onDeleteTemplate={onDeleteTemplate}
                        setCreatedTemplateSnackbarMsg={
                          setCreatedTemplateSnackbarMsg
                        }
                        logComposeEvent={logComposeEvent}
                      />
                    )}
                    {activeTab === "tones" && (
                      <TonesTab
                        voices={voices}
                        onUseVoice={onUseVoice}
                        currentVoiceID={currentVoiceID}
                        saveVoice={saveVoice}
                        onDeleteVoice={onDeleteVoice}
                        setCreatedVoiceSnackbarMsg={setCreatedVoiceSnackbarMsg}
                        voiceSettingsSnackbar={voiceSettingsSnackbar}
                        logComposeEvent={logComposeEvent}
                      />
                    )}
                    {activeTab === "prompts" && <PromptsTab />}
                  </div>
                </>
              )}
            </div>
          </Modal.Body>
        </DashThemeProvider>
        {templateSettingsSnackbar}
        <UseTemplateConfirmationModal
          isOpen={confirmationModalState.isOpen}
          onYes={handleConfirmationYes}
          onNo={handleConfirmationNo}
        />
      </Modal>
    );
  },
);

AIToolLibraryModal.displayName = "AIToolLibraryModal";
